import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Separator,
  Textarea,
} from "@repo/ui";

const LINE_PROFILE_URL = "https://line.me/R/ti/p/@jennmueng";

function openLineWithMessage(message: string) {
  const encoded = encodeURIComponent(message);
  const url = `https://line.me/R/msg/text/?${encoded}`;
  window.open(url, "_blank");
}

export function Home() {
  const nightlyFrom = "2,500 THB/night";

  return (
    <div className="min-h-screen flex flex-col">
      {/* Simple top bar */}
      <header className="w-full border-b bg-secondary/50">
        <div className="mx-auto max-w-6xl px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="size-8 rounded-full bg-primary/10" />
            <span className="font-semibold tracking-tight">Sukhumvit 24 Townhouse</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              className="bg-gradient-to-b from-primary/90 to-primary text-primary-foreground shadow-sm hover:shadow-md hover:brightness-[1.02] transition"
              asChild
            >
              <a href="#contact">Send an inquiry</a>
            </Button>
            <Button
              variant="outline"
              className="border-primary/60 text-primary hover:bg-primary/5 hover:border-primary shadow-sm transition"
              asChild
            >
              <a href={LINE_PROFILE_URL} target="_blank" rel="noreferrer">
                Message on LINE
              </a>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-b from-accent to-background">
        <div className="mx-auto max-w-6xl px-4 py-16 grid md:grid-cols-2 gap-8 items-center">
          <div className="space-y-4">
            <Badge className="bg-primary/10 text-primary">
              Bangkok • Sukhumvit 24 • Near BTS Phrom Phong
            </Badge>
            <h1 className="text-4xl md:text-5xl font-semibold tracking-tight">
              Cozy Sukhumvit 24 Townhouse
            </h1>
            <p className="text-muted-foreground">2 bedrooms • sleeps 6 • from {nightlyFrom}</p>
            <div className="flex gap-3 pt-2">
              <Button
                className="bg-gradient-to-b from-primary/90 to-primary text-primary-foreground shadow-sm hover:shadow-md hover:brightness-[1.02] transition"
                asChild
              >
                <a href="#contact">Send an inquiry</a>
              </Button>
              <Button
                variant="outline"
                className="border-primary/60 text-primary hover:bg-primary/5 hover:border-primary shadow-sm transition"
                asChild
              >
                <a href={LINE_PROFILE_URL} target="_blank" rel="noreferrer">
                  Message on LINE
                </a>
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg overflow-hidden aspect-[4/3] bg-muted">
              <img
                className="w-full h-full object-cover"
                src="https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?q=80&w=1600&auto=format&fit=crop"
                alt="Warm living room with cozy seating"
              />
            </div>
            <div className="rounded-lg overflow-hidden aspect-[4/3] bg-muted">
              <img
                className="w-full h-full object-cover"
                src="https://images.unsplash.com/photo-1505691938895-1758d7feb511?q=80&w=1600&auto=format&fit=crop"
                alt="Bright kitchen with natural light"
              />
            </div>
            <div className="rounded-lg overflow-hidden aspect-[4/3] bg-muted">
              <img
                className="w-full h-full object-cover"
                src="https://images.unsplash.com/photo-1600573472591-ee6c8e695cf5?q=80&w=1600&auto=format&fit=crop"
                alt="Comfortable bedroom with soft textiles"
              />
            </div>
            <div className="rounded-lg overflow-hidden aspect-[4/3] bg-muted">
              <img
                className="w-full h-full object-cover"
                src="https://images.unsplash.com/photo-1519710164239-da123dc03ef4?q=80&w=1600&auto=format&fit=crop"
                alt="Balcony terrace with greenery"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About + Amenities */}
      <section className="mx-auto max-w-6xl px-4 py-12 grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2 space-y-4">
          <h2 className="text-2xl font-semibold">About the Home</h2>
          <p className="text-muted-foreground leading-relaxed">
            A warm, family-friendly townhouse tucked just off Sukhumvit 24. Stroll to BTS Phrom
            Phong, EmQuartier and Emporium in minutes. Two comfortable bedrooms, a flexible living
            area that can sleep extra guests, fully equipped kitchen, and a leafy balcony for slow
            mornings. Perfect for families or small groups seeking a cozy base in the heart of
            Bangkok.
          </p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Amenities</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              {[
                "Wi‑Fi",
                "Air Conditioning",
                "Kitchen",
                "Washing machine",
                "Dryer",
                "Balcony / Terrace",
                "Parking",
                "Smart TV / Streaming",
                "Workspace / Desk",
              ].map((a) => (
                <li key={a} className="flex items-center gap-2">
                  <span className="size-1.5 rounded-full bg-primary/60" />
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </section>

      {/* Photo Gallery */}
      <section className="bg-secondary/50">
        <div className="mx-auto max-w-6xl px-4 py-12 space-y-6">
          <h2 className="text-2xl font-semibold">Photo Gallery</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?q=80&w=1600&auto=format&fit=crop",
              "https://images.unsplash.com/photo-1519710164239-da123dc03ef4?q=80&w=1600&auto=format&fit=crop",
              "https://images.unsplash.com/photo-1600573472591-ee6c8e695cf5?q=80&w=1600&auto=format&fit=crop",
              "https://images.unsplash.com/photo-1505691938895-1758d7feb511?q=80&w=1600&auto=format&fit=crop",
              "https://images.unsplash.com/photo-1600585154526-990dced4db0d?q=80&w=1600&auto=format&fit=crop",
              "https://images.unsplash.com/photo-1493809842364-78817add7ffb?q=80&w=1600&auto=format&fit=crop",
              "https://images.unsplash.com/photo-1549187774-b4e9b0445b41?q=80&w=1600&auto=format&fit=crop",
              "https://images.unsplash.com/photo-1501045661006-fcebe0257c3f?q=80&w=1600&auto=format&fit=crop",
            ].map((src, i) => (
              <div key={i} className="rounded-lg overflow-hidden aspect-[4/3] bg-muted">
                <img
                  className="w-full h-full object-cover"
                  src={src}
                  alt="Home gallery image placeholder"
                />
              </div>
            ))}
          </div>
          <p className="text-sm text-muted-foreground">
            Tasteful placeholders shown. We’ll replace with real photos soon.
          </p>
        </div>
      </section>

      {/* Location */}
      <section className="mx-auto max-w-6xl px-4 py-12 space-y-4">
        <h2 className="text-2xl font-semibold">Location</h2>
        <p className="text-muted-foreground">
          Sukhumvit 24, Bangkok — walking distance to BTS Phrom Phong, EmQuartier, and Emporium.
        </p>
        <div className="rounded-lg overflow-hidden border">
          <iframe
            title="Map of Sukhumvit 24"
            width="100%"
            height="360"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            src="https://www.google.com/maps?q=Sukhumvit%2024%2C%20Bangkok&z=16&output=embed"
          />
        </div>
      </section>

      {/* Pricing & House Rules */}
      <section className="mx-auto max-w-6xl px-4 py-12 grid md:grid-cols-3 gap-8">
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Pricing</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="text-lg">From {nightlyFrom}</div>
            <p className="text-sm text-muted-foreground">
              Cleaning fee and deposit details can be confirmed via inquiry.
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>House Rules</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              <li>Check‑in: 3:00 PM</li>
              <li>Check‑out: 11:00 AM</li>
              <li>No smoking</li>
              <li>No parties</li>
              <li>Quiet hours: 10:00 PM – 8:00 AM</li>
              <li>Pets by request</li>
            </ul>
          </CardContent>
        </Card>
      </section>

      {/* Contact */}
      <section id="contact" className="bg-secondary/50">
        <div className="mx-auto max-w-4xl px-4 py-12">
          <h2 className="text-2xl font-semibold mb-6">Contact</h2>
          <Card>
            <CardContent className="p-6">
              <form
                className="grid gap-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  const form = e.currentTarget as HTMLFormElement;
                  const formData = new FormData(form);
                  const name = String(formData.get("name") || "");
                  const dates = String(formData.get("dates") || "");
                  const guests = String(formData.get("guests") || "");
                  const message = String(formData.get("message") || "");
                  const full = `Inquiry for Sukhumvit 24 Townhouse\nName: ${name}\nDates: ${dates}\nGuests: ${guests}\nMessage: ${message}`;
                  openLineWithMessage(full);
                }}
              >
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="name">Name</Label>
                    <Input id="name" name="name" placeholder="Your full name" required />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="dates">Dates</Label>
                    <Input id="dates" name="dates" placeholder="e.g., 12–15 Nov" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="guests">Guests</Label>
                    <Input id="guests" name="guests" placeholder="e.g., 4 adults, 1 child" />
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="message">Message</Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Tell us about your trip (arrival time, questions, etc.)"
                    rows={4}
                  />
                </div>
                <div className="flex items-center gap-3 pt-2">
                  <Button
                    className="bg-gradient-to-b from-primary/90 to-primary text-primary-foreground shadow-sm hover:shadow-md hover:brightness-[1.02] transition"
                    type="submit"
                  >
                    Send inquiry via LINE
                  </Button>
                  <Separator orientation="vertical" className="h-6" />
                  <Button
                    variant="outline"
                    className="border-primary/60 text-primary hover:bg-primary/5 hover:border-primary shadow-sm transition"
                    asChild
                  >
                    <a href={LINE_PROFILE_URL} target="_blank" rel="noreferrer">
                      Open LINE profile
                    </a>
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      <footer className="mx-auto max-w-6xl px-4 py-10 text-sm text-muted-foreground">
        © {new Date().getFullYear()} Cozy Sukhumvit 24 Townhouse, Bangkok
      </footer>
    </div>
  );
}
